import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";

const DEFAULT_BADGE_STYLE: React.CSSProperties = {
  marginLeft: "8px",
  padding: "2px 6px",
  borderRadius: "999px",
  backgroundColor: "#e0f2fe",
  color: "#075985",
  fontSize: "12px",
};

const INHERITED_BADGE_STYLE: React.CSSProperties = {
  marginLeft: "8px",
  padding: "2px 6px",
  borderRadius: "999px",
  backgroundColor: "#e2e8f0",
  color: "#475569",
  fontSize: "12px",
};

const SECTION_CARD_STYLE: React.CSSProperties = {
  border: "1px solid #e5e7eb",
  borderRadius: "12px",
  padding: "14px",
  backgroundColor: "#f8fafc",
};

const MINOR_SECTION_STYLE: React.CSSProperties = {
  borderLeft: "2px solid #e2e8f0",
  paddingLeft: "12px",
};

type VersionGroup = {
  key: string;
  majorVersion: number;
  minorVersion?: number;
  versions: string[];
};

type RegistryVersionsSelectorProps = {
  /**
  Display label for this engine, e.g. "Spectronaut" or "SpectroMine" - used in button text and
  titles ("All {softwareLabel} versions", "{softwareLabel} {majorVersion}", ...).
  */
  softwareLabel: string;
  candidateVersions: string[];
  selectedVersions: string[];
  defaultVersion: string | null;
  /**
  This level's own override versions, used to badge selected versions that are only selected
  because they're inherited from a parent level rather than set here. Omit (e.g. deployment,
  which has no parent) to skip the own/inherited distinction entirely.
  */
  ownVersions?: string[];
  loading?: boolean;
  error?: string;
  disabled?: boolean;
  emptyMessage?: string;
  onSelectedVersionsChange: (versions: string[]) => void;
  onDefaultVersionChange: (version: string) => void;
};

const parseVersion = (version: string) => {
  const match = /^(?:[A-Z]{2})?(\d+)\.(\d+)\./.exec(version);
  return match ? { majorVersion: Number(match[1]), minorVersion: Number(match[2]) } : null;
};

const sortGroupKeys = (a: string, b: string) => b.localeCompare(a, undefined, { numeric: true });

const pickLatestVersion = (versions: string[]): string | null => {
  const normalized = versions.map((version) => version.trim()).filter(Boolean);
  return normalized.length === 0 ? null : ([...normalized].toSorted((a, b) => b.localeCompare(a, undefined, { numeric: true }))[0] ?? null);
};

const groupCandidateVersions = (versions: string[]): VersionGroup[] => {
  const groups = new Map<string, VersionGroup>();

  for (const version of versions) {
    const parsed = parseVersion(version);
    const key = parsed ? `${parsed.majorVersion}.${parsed.minorVersion}` : "Other";
    const group = groups.get(key) ?? {
      key,
      majorVersion: parsed?.majorVersion ?? 0,
      minorVersion: parsed?.minorVersion,
      versions: [],
    };
    group.versions.push(version);
    groups.set(key, group);
  }

  return [...groups.values()]
    .toSorted((a, b) => sortGroupKeys(a.key, b.key))
    .map((group) => ({
      ...group,
      versions: group.versions.toSorted((a, b) => b.localeCompare(a, undefined, { numeric: true })),
    }));
};

const versionButtonStyle = (isSelected: boolean, isCompact = false): React.CSSProperties => ({
  backgroundColor: isSelected ? "#dbeafe" : "#ffffff",
  borderColor: isSelected ? "#93c5fd" : "#cbd5e1",
  color: isSelected ? "#1e3a8a" : "#0f172a",
  padding: isCompact ? "4px 10px" : "6px 12px",
});

/**
Grouped-by-major.minor, searchable picker for a single registry-backed engine's versions, with
right-click-to-set-default. Used for every software whose candidate versions come from a live
registry/license feed (currently Spectronaut and SpectroMine) at every configuration level
(deployment/team/project) - see `SoftwareVersionConfigurationSection`.
*/
const RegistryVersionsSelector: React.FC<RegistryVersionsSelectorProps> = ({
  softwareLabel,
  candidateVersions,
  selectedVersions,
  defaultVersion,
  ownVersions,
  loading = false,
  error,
  disabled = false,
  emptyMessage = "No registry tags are available.",
  onSelectedVersionsChange,
  onDefaultVersionChange,
}) => {
  const [globalVersionFilter, setGlobalVersionFilter] = useState("");
  const [expandedMajorVersions, setExpandedMajorVersions] = useState<Set<number>>(new Set());

  const selectedVersionSet = useMemo(() => new Set(selectedVersions), [selectedVersions]);
  const ownVersionSet = useMemo(() => (ownVersions ? new Set(ownVersions) : null), [ownVersions]);
  const isInherited = (version: string) => ownVersionSet !== null && selectedVersionSet.has(version) && !ownVersionSet.has(version);
  const candidateVersionGroups = useMemo(() => groupCandidateVersions(candidateVersions), [candidateVersions]);
  const majorGroups = useMemo(() => {
    const groups = new Map<number, VersionGroup[]>();
    const majorGroupsOnly = candidateVersionGroups.filter((group) => group.key !== "Other");
    for (const group of majorGroupsOnly) {
      const majorVersionGroups = groups.get(group.majorVersion) ?? [];
      majorVersionGroups.push(group);
      groups.set(group.majorVersion, majorVersionGroups);
    }

    return [...groups].toSorted(([majorA], [majorB]) => majorB - majorA);
  }, [candidateVersionGroups]);
  const latestMajorVersion = majorGroups[0]?.[0] ?? null;

  useEffect(() => {
    if (latestMajorVersion === null) {
      setExpandedMajorVersions(new Set());
      return;
    }

    setExpandedMajorVersions(new Set([latestMajorVersion]));
  }, [latestMajorVersion]);

  const updateSelectedVersions = (updater: (current: Set<string>) => Set<string>) => {
    const next = updater(new Set(selectedVersionSet));
    onSelectedVersionsChange([...next].toSorted((a, b) => b.localeCompare(a, undefined, { numeric: true })));
  };

  const toggleVersions = (versions: string[]) => {
    updateSelectedVersions((current) => {
      const allSelected = versions.every((version) => current.has(version));
      for (const version of versions) {
        if (allSelected) {
          current.delete(version);
        } else {
          current.add(version);
        }
      }
      return current;
    });
  };

  const toggleVersion = (version: string) => {
    updateSelectedVersions((current) => {
      if (current.has(version)) {
        current.delete(version);
      } else {
        current.add(version);
      }
      return current;
    });
  };

  const toggleMajorExpansion = (majorVersion: number) => {
    setExpandedMajorVersions((current) => {
      const next = new Set(current);
      if (next.has(majorVersion)) {
        next.delete(majorVersion);
      } else {
        next.add(majorVersion);
      }
      return next;
    });
  };

  const allSelected = candidateVersions.length > 0 && candidateVersions.every((version) => selectedVersionSet.has(version));
  const globalFilterTerm = globalVersionFilter.trim().toLowerCase();
  const hasGlobalFilter = globalFilterTerm.length > 0;

  let viewState: "loading" | "empty" | "list" = "list";
  if (loading) {
    viewState = "loading";
  } else if (candidateVersions.length === 0) {
    viewState = "empty";
  }

  return (
    <div style={{ display: "grid", gap: "16px" }}>
      {error && (
        <div role="alert" style={{ color: "#b00020" }}>
          {error}
        </div>
      )}

      {viewState === "loading" && <p style={{ margin: 0 }}>Loading registry tags...</p>}
      {viewState === "empty" && <p style={{ margin: 0 }}>{emptyMessage}</p>}
      {viewState === "list" && (
        <>
          <div>
            <Button
              disabled={disabled}
              fillMode={allSelected ? "solid" : "outline"}
              style={versionButtonStyle(allSelected)}
              onClick={() => toggleVersions(candidateVersions)}
              onContextMenu={(event) => {
                if (disabled) {
                  return;
                }
                event.preventDefault();
                const latestVersion = pickLatestVersion(candidateVersions);
                if (latestVersion) {
                  onDefaultVersionChange(latestVersion);
                }
              }}
              title="Right-click to set latest as default"
            >
              All {softwareLabel} versions
            </Button>
          </div>

          <div style={{ maxWidth: "440px" }}>
            <input
              type="text"
              value={globalVersionFilter}
              placeholder="Search all versions ..."
              aria-label="Search all versions"
              onChange={(event) => setGlobalVersionFilter(event.target.value)}
              disabled={disabled}
              style={{
                width: "100%",
                border: "1px solid #cbd5e1",
                borderRadius: "8px",
                padding: "8px 12px",
                fontSize: "14px",
                backgroundColor: disabled ? "#f1f5f9" : "#ffffff",
              }}
            />
          </div>

          {majorGroups.map(([majorVersion, minorGroups]) => {
            const majorVersions = minorGroups.flatMap((group) => group.versions);
            const majorSelected = majorVersions.every((version) => selectedVersionSet.has(version));
            const visibleMinorGroups = hasGlobalFilter
              ? minorGroups
                  .map((group) => ({
                    ...group,
                    versions: group.versions.filter((candidate) => candidate.toLowerCase().includes(globalFilterTerm) || group.key.toLowerCase().includes(globalFilterTerm)),
                  }))
                  .filter((group) => group.versions.length > 0)
              : minorGroups;

            if (visibleMinorGroups.length === 0) {
              return null;
            }

            const isExpanded = hasGlobalFilter || expandedMajorVersions.has(majorVersion);

            return (
              <section key={majorVersion} style={SECTION_CARD_STYLE}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px", marginBottom: isExpanded ? "12px" : 0 }}>
                  <Button
                    disabled={disabled}
                    fillMode={majorSelected ? "solid" : "outline"}
                    style={versionButtonStyle(majorSelected)}
                    onClick={() => toggleVersions(majorVersions)}
                    onContextMenu={(event) => {
                      if (disabled) {
                        return;
                      }
                      event.preventDefault();
                      const latestVersion = pickLatestVersion(majorVersions);
                      if (latestVersion) {
                        onDefaultVersionChange(latestVersion);
                      }
                    }}
                    title="Right-click to set latest as default"
                  >
                    {softwareLabel} {majorVersion}
                  </Button>

                  <Button
                    disabled={disabled || hasGlobalFilter}
                    fillMode="outline"
                    style={versionButtonStyle(false, true)}
                    onClick={() => toggleMajorExpansion(majorVersion)}
                    title={isExpanded ? `Collapse ${softwareLabel} ${majorVersion}` : `Expand ${softwareLabel} ${majorVersion}`}
                  >
                    {isExpanded ? "-" : "+"}
                  </Button>
                </div>

                {!isExpanded && (
                  <p style={{ margin: 0, color: "#475569", fontSize: "13px" }}>
                    {minorGroups.length} minor versions, {majorVersions.length} builds
                  </p>
                )}

                {isExpanded && (
                  <div style={{ display: "grid", gap: "12px", paddingLeft: "10px" }}>
                    {visibleMinorGroups.map((group) => {
                      const minorSelected = group.versions.every((version) => selectedVersionSet.has(version));
                      return (
                        <section key={group.key} style={MINOR_SECTION_STYLE}>
                          <div style={{ marginBottom: "8px" }}>
                            <Button
                              disabled={disabled}
                              fillMode={minorSelected ? "solid" : "outline"}
                              style={versionButtonStyle(minorSelected)}
                              onClick={() => toggleVersions(group.versions)}
                              onContextMenu={(event) => {
                                if (disabled) {
                                  return;
                                }
                                event.preventDefault();
                                const latestVersion = pickLatestVersion(group.versions);
                                if (latestVersion) {
                                  onDefaultVersionChange(latestVersion);
                                }
                              }}
                              title="Right-click to set latest as default"
                            >
                              {group.key}
                            </Button>
                          </div>
                          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", paddingLeft: "14px" }}>
                            {group.versions.map((candidate) => (
                              <Button
                                key={candidate}
                                disabled={disabled}
                                fillMode={selectedVersionSet.has(candidate) ? "solid" : "outline"}
                                style={versionButtonStyle(selectedVersionSet.has(candidate))}
                                onClick={() => toggleVersion(candidate)}
                                onContextMenu={(event) => {
                                  if (disabled) {
                                    return;
                                  }
                                  event.preventDefault();
                                  onDefaultVersionChange(candidate);
                                  if (!selectedVersionSet.has(candidate)) {
                                    updateSelectedVersions((current) => current.add(candidate));
                                  }
                                }}
                                title="Right-click to set as default"
                              >
                                {candidate}
                                {defaultVersion === candidate && <span style={DEFAULT_BADGE_STYLE}>default</span>}
                                {isInherited(candidate) && <span style={INHERITED_BADGE_STYLE}>inherited</span>}
                              </Button>
                            ))}
                          </div>
                        </section>
                      );
                    })}
                  </div>
                )}
              </section>
            );
          })}

          {candidateVersionGroups
            .filter((group) => group.key === "Other")
            .map((group) => (
              <section key={group.key} style={{ border: "1px solid #e1e5ea", borderRadius: "6px", padding: "10px 12px" }}>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  {group.versions.map((candidate) => (
                    <Button
                      key={candidate}
                      disabled={disabled}
                      fillMode={selectedVersionSet.has(candidate) ? "solid" : "outline"}
                      style={versionButtonStyle(selectedVersionSet.has(candidate))}
                      onClick={() => toggleVersion(candidate)}
                      onContextMenu={(event) => {
                        if (disabled) {
                          return;
                        }
                        event.preventDefault();
                        onDefaultVersionChange(candidate);
                        if (!selectedVersionSet.has(candidate)) {
                          updateSelectedVersions((current) => current.add(candidate));
                        }
                      }}
                    >
                      {candidate}
                      {defaultVersion === candidate && <span style={DEFAULT_BADGE_STYLE}>default</span>}
                      {isInherited(candidate) && <span style={INHERITED_BADGE_STYLE}>inherited</span>}
                    </Button>
                  ))}
                </div>
              </section>
            ))}
        </>
      )}
    </div>
  );
};

export default RegistryVersionsSelector;
